import { makeModuleApiInfoSelector } from "@client/content/selectors";
import { apiAction } from "@client/lib/actions";
import { parseQueryString, updateUrlState } from "@client/lib/api";
import { type BaseAppState, type EntityRef } from "@client/types";
import { type HttpApiResponse } from "@client/types/Api";
import { type Dispatch } from "redux";
import api from "./api";
import { localStateSelector } from "./selectors";
import { type Search } from "./types";

export enum Actions {
  GetPageBegin = "CARTLY_SEARCH_GET_PAGE_BEGIN",
  GetPageFailure = "CARTLY_SEARCH_GET_PAGE_FAILURE",
  GetPageSuccess = "CARTLY_SEARCH_GET_PAGE_SUCCESS",

  SetSearchText = "CARTLY_SEARCH_GET_SEARCH_TEXT"
}

export type GetPageBeginActionData = EntityRef & {
  page: number;
  size: number;
  text: string;
};
export type GetPageSuccessActionData = Search.Api.GetPageResponse;
const _getPage = apiAction<BaseAppState, GetPageBeginActionData, Search.Api.GetPageResponse, GetPageSuccessActionData>({
  types: {
    begin: Actions.GetPageBegin,
    failure: Actions.GetPageFailure,
    success: Actions.GetPageSuccess
  },
  action: (
    state: BaseAppState,
    dispatch: any,
    args: GetPageBeginActionData
  ): Promise<HttpApiResponse<Search.Api.GetPageResponse>> => {
    if (!args.text) {
      return Promise.resolve({
        status: 200,
        response: {
          status: "success",
          data: {
            hasMore: false,
            items: []
          }
        }
      });
    }

    const apiInfo = makeModuleApiInfoSelector()(state, args);
    return api.getPage(apiInfo, { page: args.page, size: args.size, search: args.text });
  }
});

export const getPage = (ref: EntityRef, page: number) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  const state = getState();
  const localState = localStateSelector(state, ref);

  if (localState.status !== "Searching") {
    dispatch(
      _getPage({
        ...ref,
        page,
        size: 25,
        text: localState.text
      })
    );
  }
};

export const getPreviousPage = (ref: EntityRef) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  const state = getState();
  const localState = localStateSelector(state, ref);

  dispatch(getPage(ref, localState.currentPage - 1));
};

export const getNextPage = (ref: EntityRef) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  const state = getState();
  const localState = localStateSelector(state, ref);

  dispatch(getPage(ref, localState.currentPage + 1));
};

export const search = (ref: EntityRef, text: string) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  const state = getState();
  const localState = localStateSelector(state, ref);

  const trimmedText = text.trim();
  if (localState.status !== "Searching") {
    updateUrlState((qs) => {
      qs.search = trimmedText;
    });

    dispatch(
      _getPage({
        ...ref,
        page: 1,
        size: 25,
        text: trimmedText
      })
    );
  }
};

export const init = (data: EntityRef) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  dispatch(search({ id: data.id, source: data.source }, parseQueryString().search ?? ""));
};
